import _ from 'lodash';
import { createSlice } from '@reduxjs/toolkit';
import { cursorMaker } from 'utils/cursorMaker';

interface videoItem {
  [key: string]: any;
}

interface videoList {
  [key: string]: videoItem[];
}

interface pageUrl {
  [key: string]: string | null | undefined;
}

interface pageDate {
  [key: string]: string | null | undefined;
}

export type VideoState = {
  allVideos: any;
  videos: videoList;
  newlyAddedVideos: videoList;
  nextUrl: pageUrl;
  prevUrl: pageUrl;
  nextDate: pageDate;
  prevDate: pageDate;
  videoPrevNextGroup: any;
  newPrevVideoId: any;
  newNextVideoId: any;
  videoPageLoaded: any;
};

const initialState: VideoState = {
  allVideos: {},
  videos: {},
  newlyAddedVideos: {},
  nextUrl: {},
  prevUrl: {},
  nextDate: {},
  prevDate: {},
  videoPrevNextGroup: {},
  newPrevVideoId: {},
  newNextVideoId: {},
  videoPageLoaded: {}
};
const pageSize = process.env.REACT_APP_PAGE_SIZE || 5;
const pageKeepSize = Number(process.env.REACT_APP_VIDEO_PAGE_KEEP_SIZE) || 100;

const videoSlice = createSlice({
  name: 'video',
  initialState,
  reducers: {
    setVideos(state, action) {
      if (!state.videos[action.payload.key]) {
        state.videos[action.payload.key] = [];
      }
      if (!state.allVideos[action.payload.key]) {
        state.allVideos[action.payload.key] = [];
      }
      if (action.payload.data.next_date) {
        if (
          state.nextDate[action.payload.key] &&
          new Date(action.payload.data.next_date).getTime() >
            new Date(state.nextDate[action.payload.key] as string).getTime()
        ) {
          state.nextDate[action.payload.key] = action.payload.data.next_date;
          state.nextUrl[action.payload.key] = action.payload.data.links.next;
        }
      } else {
        state.nextDate[action.payload.key] = null;
        state.nextUrl[action.payload.key] = null;
      }
      if (action.payload.data.previous_date) {
        if (state.prevDate[action.payload.key]) {
          if (
            new Date(action.payload.data.previous_date).getTime() <
            new Date(state.prevDate[action.payload.key] as string).getTime()
          ) {
            state.prevDate[action.payload.key] = action.payload.data.previous_date;
            state.prevUrl[action.payload.key] = action.payload.data.links.previous;
          }
        } else {
          if (!action.payload.scrollToTop || action.payload.scrollToTop === undefined) {
            state.prevDate[action.payload.key] = action.payload.data.previous_date;
            state.prevUrl[action.payload.key] = action.payload.data.links.previous;
          } else {
            state.prevDate[action.payload.key] = null;
            state.prevUrl[action.payload.key] = null;
          }
        }
      } else {
        state.prevDate[action.payload.key] = null;
        state.prevUrl[action.payload.key] = null;
      }
      if (!state.videoPrevNextGroup[action.payload.key]) {
        state.videoPrevNextGroup[action.payload.key] = [];
      }

      // find group to extend
      const groupIndex = state.videoPrevNextGroup[action.payload.key].findIndex(
        (groupData: any) =>
          groupData.prevDate === state.prevDate[action.payload.key] ||
          groupData.nextDate === state.nextDate[action.payload.key]
      );
      // if (groupIndex !== -1) extend the group
      const newVideoPrevNextGroup = [];
      const newlyAddedVideos = action.payload.data.results.reverse();
      if (!state.newPrevVideoId[action.payload.key] && !state.newNextVideoId[action.payload.key]) {
        state.newPrevVideoId[action.payload.key] = newlyAddedVideos?.[0]?.id;
        state.newNextVideoId[action.payload.key] = newlyAddedVideos?.[action.payload.data.results.length - 1]?.id;
      } else {
        if (action.payload.scrollToTop) {
          state.newNextVideoId[action.payload.key] = newlyAddedVideos?.[action.payload.data.results.length - 1]?.id;
        } else {
          state.newPrevVideoId[action.payload.key] = newlyAddedVideos?.[0]?.id;
        }
      }
      const newItem = {
        prevDate: state.prevDate[action.payload.key],
        prevUrl: state.prevUrl[action.payload.key],
        nextDate: state.nextDate[action.payload.key],
        nextUrl: state.nextUrl[action.payload.key]
      };
      if (groupIndex !== -1) {
        state.videoPrevNextGroup[action.payload.key].splice(groupIndex, 1);
      }
      for (let i = 0; i < state.videoPrevNextGroup[action.payload.key].length; i++) {
        if (
          ((newItem.prevDate &&
            new Date(state.videoPrevNextGroup[action.payload.key][i].prevDate).getTime() >=
              new Date(newItem.prevDate).getTime()) ||
            !newItem.prevDate) &&
          newItem.nextDate &&
          new Date(state.videoPrevNextGroup[action.payload.key][i].prevDate).getTime() <=
            new Date(newItem.nextDate).getTime()
        ) {
          if (
            new Date(state.videoPrevNextGroup[action.payload.key][i].nextDate).getTime() >
            new Date(newItem.nextDate).getTime()
          ) {
            newItem.nextDate = state.videoPrevNextGroup[action.payload.key][i].nextDate;
            newItem.nextUrl = state.videoPrevNextGroup[action.payload.key][i].nextUrl;
          }
        } else if (
          ((newItem.prevDate &&
            new Date(state.videoPrevNextGroup[action.payload.key][i].prevDate).getTime() <
              new Date(newItem.prevDate).getTime()) ||
            !state.videoPrevNextGroup[action.payload.key][i].prevDate) &&
          ((newItem.prevDate &&
            new Date(state.videoPrevNextGroup[action.payload.key][i].nextDate).getTime() >
              new Date(newItem.prevDate).getTime()) ||
            !state.videoPrevNextGroup[action.payload.key][i].nextDate)
        ) {
          newItem.prevDate = state.videoPrevNextGroup[action.payload.key][i].prevDate;
          newItem.prevUrl = state.videoPrevNextGroup[action.payload.key][i].prevUrl;
          if (
            newItem.nextDate &&
            new Date(newItem.nextDate).getTime() <
              new Date(state.videoPrevNextGroup[action.payload.key][i].nextDate).getTime()
          ) {
            newItem.nextDate = state.videoPrevNextGroup[action.payload.key][i].nextDate;
            newItem.nextUrl = state.videoPrevNextGroup[action.payload.key][i].nextUrl;
          }
        } else {
          newVideoPrevNextGroup.push(state.videoPrevNextGroup[action.payload.key][i]);
        }
      }
      const allVideos = _.uniqBy(
        [...state.allVideos[action.payload.key], ...action.payload.data.results],
        (o) => o.id
      ).sort((objA: any, objB: any) => objB.id - objA.id);

      const filteredVideos = allVideos.filter((video) => {
        if (newItem.nextDate && newItem.prevDate) {
          return (
            new Date(video.date_added).getTime() > new Date(newItem.prevDate as string).getTime() &&
            new Date(video.date_added).getTime() < new Date(newItem.nextDate as string).getTime()
          );
        } else if (newItem.nextDate) {
          return new Date(video.date_added).getTime() < new Date(newItem.nextDate as string).getTime();
        } else if (newItem.prevDate) {
          return new Date(video.date_added).getTime() > new Date(newItem.prevDate as string).getTime();
        } else {
          return true;
        }
      });

      // const userData = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const serializedWorkspaceState = localStorage.getItem('workspaces') || '[]';
      const workpaceState = JSON.parse(serializedWorkspaceState);
      const lastActiveWorkspace = localStorage.getItem('last-active:workspace');
      const sessionWorkspace = sessionStorage.getItem('workspace') || lastActiveWorkspace;
      const activeWorkspace = workpaceState.find((workspace: any) => workspace.workspace_id === sessionWorkspace);

      let newVideos = null as any;
      if (filteredVideos.length > pageKeepSize) {
        if (action.payload.scrollToTop) {
          newVideos = filteredVideos.slice(0, pageKeepSize);
          state.nextDate[action.payload.key] = newItem.nextDate;
          state.nextUrl[action.payload.key] = newItem.nextUrl;
          state.prevDate[action.payload.key] = filteredVideos[pageKeepSize].date_added;
          state.prevUrl[action.payload.key] = `${activeWorkspace?.url}/${
            action.payload.key === 'home'
              ? 'api/videos/recent-uploads/'
              : `api/videos/channel/${action.payload.key}/vods/`
          }?cursor=${window.btoa(
            cursorMaker(new Date(state.prevDate[action.payload.key] as string), true)
          )}&page_size=${pageSize}`;
          state.newPrevVideoId[action.payload.key] = newVideos[pageKeepSize - 1].id;
          if (!state.nextUrl[action.payload.key]) {
            state.newNextVideoId[action.payload.key] = newVideos[0].id;
          }
          newItem.prevDate = state.prevDate[action.payload.key];
          newItem.prevUrl = state.prevUrl[action.payload.key];
        } else {
          newVideos = filteredVideos.slice(filteredVideos.length - pageKeepSize, filteredVideos.length);
          state.prevDate[action.payload.key] = newItem.prevDate;
          state.prevUrl[action.payload.key] = newItem.prevUrl;
          state.nextDate[action.payload.key] = filteredVideos[filteredVideos.length - pageKeepSize - 1].date_added;
          state.nextUrl[action.payload.key] = `${activeWorkspace?.url}/${
            action.payload.key === 'home'
              ? 'api/videos/recent-uploads/'
              : `api/videos/channel/${action.payload.key}/vods/`
          }?cursor=${window.btoa(
            cursorMaker(new Date(state.nextDate[action.payload.key] as string), false)
          )}&page_size=${pageSize}`;
          state.newNextVideoId[action.payload.key] = newVideos[0].id;
          if (!state.prevUrl[action.payload.key]) {
            state.newPrevVideoId[action.payload.key] = newVideos[pageKeepSize - 1].id;
          }
          newItem.nextDate = state.nextDate[action.payload.key];
          newItem.nextUrl = state.nextUrl[action.payload.key];
        }
      } else {
        state.prevDate[action.payload.key] = newItem.prevDate;
        state.prevUrl[action.payload.key] = newItem.prevUrl;
        state.nextDate[action.payload.key] = newItem.nextDate;
        state.nextUrl[action.payload.key] = newItem.nextUrl;
      }
      newVideoPrevNextGroup.push(newItem);
      state.videoPrevNextGroup[action.payload.key] = [...newVideoPrevNextGroup].sort(
        (objA: any, objB: any) => new Date(objA.prevDate).getTime() - new Date(objB.prevDate).getTime()
      );
      state.allVideos[action.payload.key] = allVideos;
      state.videos[action.payload.key] = newVideos ? newVideos : filteredVideos;
    },
    loadSpecificVideo(state, action) {
      if (!state.videoPrevNextGroup[action.payload.key]) {
        state.videoPrevNextGroup[action.payload.key] = [];
      }
      if (!state.videos[action.payload.key]) {
        state.videos[action.payload.key] = [];
      }
      if (!state.allVideos[action.payload.key]) {
        state.allVideos[action.payload.key] = [];
      }
      if (action.payload.data.next_date) {
        state.nextDate[action.payload.key] = action.payload.data.next_date;
        state.nextUrl[action.payload.key] = action.payload.data.links.next;
      } else {
        state.nextDate[action.payload.key] = null;
        state.nextUrl[action.payload.key] = null;
      }

      if (action.payload.data.previous_date) {
        state.prevDate[action.payload.key] = action.payload.data.previous_date;
        state.prevUrl[action.payload.key] = action.payload.data.links.previous;
      } else {
        state.prevDate[action.payload.key] = null;
        state.prevUrl[action.payload.key] = null;
      }
      // if (groupIndex !== -1) extend the group
      const newVideoPrevNextGroup = [];
      const newlyAddedVideos = action.payload.data.results.reverse();
      state.newPrevVideoId[action.payload.key] = newlyAddedVideos?.[0].id;
      state.newNextVideoId[action.payload.key] = newlyAddedVideos?.[action.payload.data.results.length - 1].id;
      const newItem = {
        prevDate: state.prevDate[action.payload.key],
        prevUrl: state.prevUrl[action.payload.key],
        nextDate: state.nextDate[action.payload.key],
        nextUrl: state.nextUrl[action.payload.key]
      };
      for (let i = 0; i < state.videoPrevNextGroup[action.payload.key].length; i++) {
        if (
          ((newItem.prevDate &&
            new Date(state.videoPrevNextGroup[action.payload.key][i].prevDate).getTime() >=
              new Date(newItem.prevDate).getTime()) ||
            !newItem.prevDate) &&
          newItem.nextDate &&
          new Date(state.videoPrevNextGroup[action.payload.key][i].prevDate).getTime() <=
            new Date(newItem.nextDate).getTime()
        ) {
          if (
            new Date(state.videoPrevNextGroup[action.payload.key][i].nextDate).getTime() >
            new Date(newItem.nextDate).getTime()
          ) {
            newItem.nextDate = state.videoPrevNextGroup[action.payload.key][i].nextDate;
            newItem.nextUrl = state.videoPrevNextGroup[action.payload.key][i].nextUrl;
          }
        } else if (
          ((newItem.prevDate &&
            new Date(state.videoPrevNextGroup[action.payload.key][i].prevDate).getTime() <
              new Date(newItem.prevDate).getTime()) ||
            !state.videoPrevNextGroup[action.payload.key][i].prevDate) &&
          ((newItem.prevDate &&
            new Date(state.videoPrevNextGroup[action.payload.key][i].nextDate).getTime() >
              new Date(newItem.prevDate).getTime()) ||
            !state.videoPrevNextGroup[action.payload.key][i].nextDate)
        ) {
          newItem.prevDate = state.videoPrevNextGroup[action.payload.key][i].prevDate;
          newItem.prevUrl = state.videoPrevNextGroup[action.payload.key][i].prevUrl;
          if (
            newItem.nextDate &&
            new Date(newItem.nextDate).getTime() <
              new Date(state.videoPrevNextGroup[action.payload.key][i].nextDate).getTime()
          ) {
            newItem.nextDate = state.videoPrevNextGroup[action.payload.key][i].nextDate;
            newItem.nextUrl = state.videoPrevNextGroup[action.payload.key][i].nextUrl;
          }
        } else {
          newVideoPrevNextGroup.push(state.videoPrevNextGroup[action.payload.key][i]);
        }
      }
      const allVideos = _.uniqBy(
        [...state.allVideos[action.payload.key], ...action.payload.data.results],
        (o) => o.id
      ).sort((objA: any, objB: any) => objB.id - objA.id);

      const filteredVideos = allVideos.filter((video) => {
        if (newItem.nextDate && newItem.prevDate) {
          return (
            new Date(video.date_added).getTime() >= new Date(newItem.prevDate as string).getTime() &&
            new Date(video.date_added).getTime() <= new Date(newItem.nextDate as string).getTime()
          );
        } else if (newItem.nextDate) {
          return new Date(video.date_added).getTime() <= new Date(newItem.nextDate as string).getTime();
        } else if (newItem.prevDate) {
          return new Date(video.date_added).getTime() >= new Date(newItem.prevDate as string).getTime();
        } else {
          return true;
        }
      });
      state.prevDate[action.payload.key] = newItem.prevDate;
      state.prevUrl[action.payload.key] = newItem.prevUrl;
      state.nextDate[action.payload.key] = newItem.nextDate;
      state.nextUrl[action.payload.key] = newItem.nextUrl;
      newVideoPrevNextGroup.push(newItem);
      state.videoPrevNextGroup[action.payload.key] = [...newVideoPrevNextGroup].sort(
        (objA: any, objB: any) => new Date(objA.prevDate).getTime() - new Date(objB.prevDate).getTime()
      );
      state.allVideos[action.payload.key] = allVideos;
      state.videos[action.payload.key] = filteredVideos;
    },
    addVideo(state, action) {
      if (!state.videos[action.payload.key]) {
        state.videos[action.payload.key] = [];
      }
      state.videos[action.payload.key] = _.uniqBy(
        [...state.videos[action.payload.key], action.payload.data],
        (o) => o.id
      ).sort((objA: any, objB: any) => new Date(objB.date_added).getTime() - new Date(objA.date_added).getTime());
      if (!state.newlyAddedVideos[action.payload.key]) {
        state.newlyAddedVideos[action.payload.key] = [];
      }
      state.newlyAddedVideos[action.payload.key].push(action.payload.data);
    },
    editVideo(state, action) {
      if (!state.videos[action.payload.key]) {
        state.videos[action.payload.key] = [];
      }
      state.videos[action.payload.key] = state.videos[action.payload.key].map((video) => {
        if (video.id === action.payload.id) {
          return action.payload.data;
        }
        return video;
      });
    },
    removeVideo(state, action) {
      if (!state.videos[action.payload.key]) {
        state.videos[action.payload.key] = [];
      }
      state.videos[action.payload.key] = state.videos[action.payload.key].filter(
        (video) => video.id !== action.payload.data
      );
      if (!state.newlyAddedVideos[action.payload.key]) {
        state.newlyAddedVideos[action.payload.key] = [];
      }
      state.newlyAddedVideos[action.payload.key] = state.newlyAddedVideos[action.payload.key].filter(
        (addedVideo) => addedVideo.id !== action.payload.data
      );
    },
    initNewVideos(state, action) {
      state.newlyAddedVideos[action.payload] = [];
    },
    setRecentVideoPrevNextGroup(state, action) {
      if (!state.videoPrevNextGroup[action.payload.key]) {
        state.videoPrevNextGroup[action.payload.key] = [];
      }
      const currentVideoPrevNextGroup = state.videoPrevNextGroup[action.payload.key].find(
        (group: any) => group.nextDate === null
      );
      if (currentVideoPrevNextGroup) {
        state.newPrevVideoId[action.payload.key] = currentVideoPrevNextGroup.firstVideoId;
        state.nextDate[action.payload.key] = currentVideoPrevNextGroup.nextDate;
        state.nextUrl[action.payload.key] = currentVideoPrevNextGroup.nextUrl;
        state.prevDate[action.payload.key] = currentVideoPrevNextGroup.prevDate;
        state.prevUrl[action.payload.key] = currentVideoPrevNextGroup.prevUrl;
      } else {
        if (state.videos[action.payload.key]) {
          const firstVideoId =
            state.videos[action.payload.key]?.[state.videos[action.payload.key].length - Number(pageSize)]?.id;
          state.newPrevVideoId[action.payload.key] = firstVideoId;
          state.nextDate[action.payload.key] = null;
          state.nextUrl[action.payload.key] = null;
          state.prevDate[action.payload.key] =
            state.videos[action.payload.key][
              state.videos[action.payload.key].length - Number(pageSize) - 1
            ]?.date_added;
          state.prevUrl[action.payload.key] = `${
            action.payload.key === 'home'
              ? 'api/videos/recent-uploads/'
              : `api/videos/channel/${action.payload.key}/vods/`
          }?cursor=${window.btoa(
            cursorMaker(
              new Date(
                state.videos[action.payload.key][
                  state.videos[action.payload.key].length - Number(pageSize) - 2
                ]?.date_added
              ),
              true
            )
          )}&page_size=${pageSize}`;
        }
      }
    },
    setPageVideoLoad(state, action) {
      state.videoPageLoaded[action.payload.key] = true;
    }
  }
});

export const {
  setVideos,
  loadSpecificVideo,
  addVideo,
  editVideo,
  removeVideo,
  initNewVideos,
  setRecentVideoPrevNextGroup,
  setPageVideoLoad
} = videoSlice.actions;

export const videoReducer = videoSlice.reducer;
